/**
 * Formatting helpers for the Timesheet Approvals workspace.
 *
 * Templates render values, they never calculate them (see CLAUDE.md), so every day label,
 * duration, plural and sentence on the workspace comes from a named function here.
 *
 * Durations come from the shared `helpers/duration-formatting.js` and dates/moments from
 * `helpers/date-formatting.js`. Week and day arithmetic reuses `edit-timesheet2/day-navigation.js`.
 * None of that is re-implemented here: this file only composes them into the workspace's wording.
 */

import { formatSeconds } from "/helpers/duration-formatting.js";
import { formatDisplayMoment } from "/helpers/date-formatting.js";
import { addDays, formatDayWithMonth, formatMonthLabel, parseIsoDate, toIsoDate } from "/module.timesheets/edit-timesheet2/day-navigation.js";
import { formatCount } from "/module.timesheets/edit-timesheet2/timesheet2-formatting.js";


const EM_DASH = "—";

const FULL_WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];




//-- Days and weeks

/**
 * "Wed, 8 Jul" — a column heading or a short reference to a day.
 */
export function formatDayLabel(isoDate)
{
	if (!isoDate) return EM_DASH;

	return formatDayWithMonth(isoDate);
}


/**
 * "Wednesday, 8 July 2026" — the Day review header.
 */
export function formatLongDayLabel(isoDate)
{
	if (!isoDate) return EM_DASH;

	const date = parseIsoDate(isoDate);

	return FULL_WEEKDAY_NAMES[date.getDay()] + ", " + date.getDate() + " " + formatMonthLabel(date.getFullYear(), date.getMonth());
}


/**
 * The range a week column set covers: "6 – 10 July 2026", "29 June – 3 July 2026" or
 * "29 December 2026 – 2 January 2027". Pass the first and last dates actually shown (Mon–Fri, or
 * Mon–Sun when the weekend carries time).
 */
export function formatWeekRangeLabel(startIsoDate, endIsoDate)
{
	if (!startIsoDate || !endIsoDate) return EM_DASH;

	const start = parseIsoDate(startIsoDate);
	const end = parseIsoDate(endIsoDate);

	const startMonth = formatMonthLabel(start.getFullYear(), start.getMonth()).split(" ")[0];
	const endMonth = formatMonthLabel(end.getFullYear(), end.getMonth()).split(" ")[0];

	if (start.getFullYear() !== end.getFullYear())
	{
		return start.getDate() + " " + startMonth + " " + start.getFullYear() + " – " + end.getDate() + " " + endMonth + " " + end.getFullYear();
	}

	if (start.getMonth() !== end.getMonth())
	{
		return start.getDate() + " " + startMonth + " – " + end.getDate() + " " + endMonth + " " + end.getFullYear();
	}

	return start.getDate() + " – " + end.getDate() + " " + endMonth + " " + end.getFullYear();
}


/**
 * The date (yyyy-MM-dd) `days` away from `isoDate` (negative = earlier).
 */
export function shiftDays(isoDate, days)
{
	return toIsoDate(addDays(parseIsoDate(isoDate), days));
}


/**
 * The week start (yyyy-MM-dd) `weeks` away from `weekStartIsoDate` (negative = earlier).
 */
export function shiftWeek(weekStartIsoDate, weeks)
{
	return shiftDays(weekStartIsoDate, weeks * 7);
}




/**
 * The weeks of the Approvals window as drop-down options, newest first: `{ value: "2026-07-06", label: "6 – 12 July 2026" }`,
 * the value being the week's Monday. The newest (the current week) says so.
 */
export function buildWeekOptions(windowStartIso, windowEndIso)
{
	const options = [];

	if (!windowStartIso || !windowEndIso) return options;

	for (let start = windowStartIso; start <= windowEndIso; start = shiftWeek(start, 1))
	{
		options.push({ value: start, label: formatWeekRangeLabel(start, shiftDays(start, 6)) });
	}

	options.reverse();

	if (options.length > 0) options[0].label = options[0].label + " (this week)";

	return options;
}




/**
 * "Mon" — the weekday part of a matrix column heading.
 */
export function formatWeekdayShort(isoDate)
{
	if (!isoDate) return EM_DASH;

	return formatDayWithMonth(isoDate).split(",")[0];
}


/**
 * "8 Jul" — the date part of a matrix column heading.
 */
export function formatDayMonth(isoDate)
{
	if (!isoDate) return EM_DASH;

	return formatDayWithMonth(isoDate).split(",")[1].trim();
}




//-- Counts and plurals

export function formatEntryCount(count)
{
	return formatCount(count, "entry", "entries");
}


export function formatPeopleCount(count)
{
	return formatCount(count, "person", "people");
}


export function formatDayCount(count)
{
	return formatCount(count, "day");
}


/**
 * "no reminders" / "1 reminder" / "3 reminders".
 */
export function formatReminderCount(count)
{
	if (!count) return "no reminders";

	return formatCount(count, "reminder");
}


/**
 * "Reopened 2×" — how many times a day was edited after it was approved.
 */
export function formatReopenedCount(count)
{
	return "Reopened " + (count || 0) + "×";
}




/**
 * The period the Approvals window covers, for a sentence: "the last 5 weeks" (the number is configurable, see
 * `Timesheets.ApprovalsWindowWeeks`), or "this week" when the window is one week.
 */
export function formatWindowPhrase(windowWeeks)
{
	return windowWeeks > 1 ? "the last " + windowWeeks + " weeks" : "this week";
}


/**
 * What a screen reader hears for the Approvals nav badge: "3 days awaiting approval".
 */
export function formatPendingBadgeLabel(count)
{
	return formatDayCount(count) + " awaiting approval";
}


/**
 * What a screen reader hears for the Home nav badge: "3 days to submit in the last 5 weeks".
 */
export function formatMyOutstandingBadgeLabel(count, windowWeeks)
{
	return formatDayCount(count) + " to submit in " + formatWindowPhrase(windowWeeks);
}


/**
 * The hover text on the Home nav badge: what the number counts, and what it leaves out.
 */
export function formatMyOutstandingBadgeTooltip(count, windowWeeks)
{
	return formatDayCount(count) + " of your timesheets still to submit. This counts your Draft and Rejected days from " + formatWindowPhrase(windowWeeks)
		+ ", not including today (a day isn't outstanding until it's over)."
		+ " Older unsubmitted days are not included here; the \"to submit in total\" button on the timesheet page counts them all.";
}


/**
 * The hover text on the Approvals nav badge.
 */
export function formatPendingBadgeTooltip(count, windowWeeks)
{
	return formatDayCount(count) + " awaiting your approval. This counts days submitted by the people you review, from " + formatWindowPhrase(windowWeeks) + ".";
}




//-- Durations and moments

/**
 * A day's total, or an em-dash when there is none to show (a Not-submitted day).
 */
export function formatDayTotal(seconds)
{
	if (seconds === null || seconds === undefined) return EM_DASH;

	return formatSeconds(seconds);
}


/**
 * A row's total in the matrix: the sum of its handed-over days, or an em-dash when it has none.
 */
export function formatRowTotal(seconds)
{
	if (!seconds) return EM_DASH;

	return formatSeconds(seconds);
}


/**
 * What a screen reader hears for a matrix cell: "Pending, 5h 16m, differs from what capture produced, reopened 2×".
 */
export function formatDayCellSpoken(statusLabel, totalLabel, differs, reopenCount)
{
	const parts = [statusLabel];

	if (totalLabel !== EM_DASH) parts.push(totalLabel);
	if (differs) parts.push("differs from what capture produced");
	if (reopenCount > 0) parts.push(formatReopenedCount(reopenCount).toLowerCase());

	return parts.join(", ");
}


/**
 * When an event happened, or "before change tracking" for history from before changes were
 * recorded (the API sends a null date for those).
 */
export function formatEventWhen(dateUtc)
{
	if (!dateUtc) return "before change tracking";

	return formatDisplayMoment(dateUtc);
}


/**
 * The words that go with a step of the review history: a rejection's reason and comment, a resubmission's note to the approver.
 * Blank for a step with none.
 */
export function formatReviewEventDetail(event)
{
	if (event.Action === "Rejected")
	{
		const reason = event.ReasonLabel || "no reason recorded";

		return event.Comment ? reason + " — “" + event.Comment + "”" : reason;
	}

	if (event.Action === "Resubmitted")
	{
		return event.ResponseNote ? "Note to approver: “" + event.ResponseNote + "”" : "No note added";
	}

	return "";
}


/**
 * The "Re-submitted" badge on a day waiting for approval: "Re-submitted" once, "Re-submitted ×2" after a second time.
 */
export function formatResubmittedBadge(count)
{
	return count > 1 ? "Re-submitted ×" + count : "Re-submitted";
}


/**
 * The heading of the review history: "Review history (5 steps)".
 */
export function formatReviewHistoryHeading(stepCount)
{
	return "Review history (" + formatCount(stepCount, "step") + ")";
}



/**
 * A moment as DD-Mmm-YY, h:mm AM/PM, in the reader's own timezone.
 */
export function formatApprovalMoment(dateUtc)
{
	return formatDisplayMoment(dateUtc);
}




//-- Sentences

/**
 * "4 pending · 12 approved · 1 rejected · 3 not submitted" — the Overview's status summary.
 */
export function formatStatusSummary(counts)
{
	const c = counts || {};

	return (c.Pending || 0) + " pending · " + (c.Approved || 0) + " approved · " + (c.Rejected || 0) + " rejected · " + (c.NotSubmitted || 0) + " not submitted";
}


/**
 * The Day review's sub-line: "Senior Project Manager · Wednesday, 8 July 2026 · 5h 16m logged".
 */
export function formatDayReviewLine(roleName, isoDate, totalSeconds)
{
	const parts = [];

	if (roleName) parts.push(roleName);
	parts.push(formatLongDayLabel(isoDate));
	parts.push(formatSeconds(totalSeconds) + " logged");

	return parts.join(" · ");
}


/**
 * The sign-off line above Approve / Reject.
 */
export function formatSignOffSentence(totalSeconds, entryCount)
{
	return "You're signing off on " + formatSeconds(totalSeconds) + " across " + formatEntryCount(entryCount)
		+ ". You can't edit — send back with a reason if something's wrong.";
}


/**
 * "✓ Approved by Dominic Dodwell, 08-Jul-26, 9:14 AM · bulk": the Approved state of the action bar.
 * `mannerSuffix` is the display map's suffix (" · bulk", " · automatic" or "").
 */
export function formatApprovedLine(review, mannerSuffix)
{
	const r = review || {};
	const who = r.ApprovedByName ? " by " + r.ApprovedByName : "";
	const when = r.DateApprovedUTC ? ", " + formatDisplayMoment(r.DateApprovedUTC) : "";

	return "✓ Approved" + who + when + (mannerSuffix || "");
}


/**
 * The read-only line on a Rejected day.
 */
export function formatRejectedWaitingLine(name)
{
	return "Sent back to " + name + " — waiting for them to resubmit.";
}


/**
 * The activity captured after a day was handed over, held back and not included (D-18).
 */
export function formatHeldActivityNotice(seconds)
{
	return "Activity held since submission: " + formatSeconds(seconds) + " (not included)";
}


/**
 * "n of N pending days selected" — the Bulk approve header.
 */
export function formatSelectionSummary(selectedCount, totalCount)
{
	return selectedCount + " of " + totalCount + " pending " + (totalCount === 1 ? "day" : "days") + " selected";
}


/**
 * The sentence in the bulk confirmation: "You're about to approve 3 days across 2 people. Each
 * approval is a sign-off attributed to you."
 */
export function formatBulkConfirmSentence(dayCount, personCount)
{
	return "You're about to approve " + formatDayCount(dayCount) + " across " + formatPeopleCount(personCount)
		+ ". Each approval is a sign-off attributed to you.";
}


/**
 * The Overview's backlog banner: "11 days from earlier periods still need your action — 4 pending,
 * 1 rejected, 6 not submitted. Oldest: 15 Jun".
 */
export function formatBacklogBanner(backlog)
{
	const b = backlog || {};

	let text = formatDayCount(b.Days || 0) + " from earlier periods still " + (b.Days === 1 ? "needs" : "need") + " your action — "
		+ (b.Pending || 0) + " pending, " + (b.Rejected || 0) + " rejected, " + (b.NotSubmitted || 0) + " not submitted.";

	if (b.OldestDate) text += " Oldest: " + formatDayMonth(b.OldestDate);

	return text;
}


/**
 * The banner on the Overview while it shows earlier periods.
 */
export function formatEarlierPeriodsBanner(windowWeeks)
{
	return "Showing earlier periods — every past day in " + formatWindowPhrase(windowWeeks) + " that still needs action";
}


/**
 * "↳ From split · Meridian → Northwind": the tag on a part of a split captured block.
 */
export function formatSplitTag(splitTag)
{
	if (!splitTag) return "";

	return "↳ From split · " + (splitTag.BaseClientName || EM_DASH) + " → " + (splitTag.ClientName || EM_DASH);
}


/**
 * One side of a variance view: "Meridian Legal · Litigation", or the allocation label when there is no client.
 */
export function formatVarianceSide(side)
{
	if (!side) return EM_DASH;

	if (side.ClientName) return side.MatterLabel ? side.ClientName + " · " + side.MatterLabel : side.ClientName;

	return side.AllocationLabel || EM_DASH;
}


/**
 * An entry's heading: "Client · matter", falling back to the allocation label.
 */
export function formatEntryHeading(entry)
{
	return formatVarianceSide(entry);
}


/**
 * The short strip on a flagged entry in the bulk list: "Captured: Meridian → Now: Northwind", or
 * "Added manually → Now: …" when there was no captured side.
 */
export function formatVarianceStrip(variance)
{
	if (!variance) return "";

	const captured = variance.BaseLabel ? "Captured: " + variance.BaseLabel : "Not captured — added manually";

	return captured + " → Now: " + (variance.NowLabel || EM_DASH);
}


/**
 * "Split & reallocated · changed by Amara Okafor, 08-Jul-26, 9:14 AM": the footer under a variance view.
 */
export function formatProvenance(mechanismLabel, changedByName, dateUtc)
{
	const parts = [mechanismLabel];

	if (changedByName) parts.push("changed by " + changedByName + (dateUtc ? ", " + formatDisplayMoment(dateUtc) : ""));

	return parts.join(" · ");
}


/**
 * "3 captured blocks removed by Haylie Logan (42m)": the row for captured time the owner removed.
 */
export function formatRemovedCapturedLine(count, seconds, byName)
{
	return count + " captured " + (count === 1 ? "block" : "blocks") + " removed by " + (byName || "the timekeeper") + " (" + formatSeconds(seconds) + ")";
}


/**
 * "n entries differ from what capture originally produced."
 */
export function formatVarianceBanner(entryCount)
{
	return formatEntryCount(entryCount) + (entryCount === 1 ? " differs" : " differ") + " from what capture originally produced. Expand an entry to see the change.";
}


/**
 * "3 entries in this batch differ from base": the pill on the Bulk approve header.
 */
export function formatBatchDifferPill(entryCount)
{
	return formatEntryCount(entryCount) + " in this batch " + (entryCount === 1 ? "differs" : "differ") + " from base";
}


/**
 * The Reject & return modal's intro.
 */
export function formatRejectModalIntro(name, dayLabel)
{
	return "Sending " + name + "'s " + dayLabel + " back for correction. They'll fix and resubmit — you can't edit it yourself.";
}


/**
 * "Approved · Haylie Logan, Wed, 8 Jul"
 */
export function formatApprovedToast(name, isoDate)
{
	return "Approved · " + name + ", " + formatDayLabel(isoDate);
}


/**
 * "Sent back to Haylie Logan — Missing time or gaps in the day"
 */
export function formatSentBackToast(name, reasonLabel)
{
	return "Sent back to " + name + " — " + reasonLabel;
}


/**
 * "Approved 3 days · bulk"
 */
export function formatBulkApprovedToast(dayCount)
{
	return "Approved " + formatDayCount(dayCount) + " · bulk";
}


/**
 * What a bulk approval skipped: "2 days were not approved: 1 changed since you opened it, 1 no longer pending".
 * `parts` is `[{ Label, Count }]`.
 */
export function formatSkippedSummary(parts)
{
	const total = parts.reduce((sum, p) => sum + p.Count, 0);

	return formatDayCount(total) + (total === 1 ? " was" : " were") + " not approved: "
		+ parts.map((p) => p.Count + " " + p.Label.toLowerCase()).join(", ");
}


export function formatNudgeSentToast(name)
{
	return "Nudge sent to " + name;
}


export function formatNudgedManyToast(personCount)
{
	return "Nudged " + formatPeopleCount(personCount) + " with outstanding time";
}


/**
 * The body of the single-person nudge confirmation (decided 2026-10-03: an email cannot be unsent, so one nudge
 * asks first too).
 */
export function formatNudgeOneConfirm(name)
{
	return "This sends " + name + " an email listing the days they haven't submitted.";
}


/**
 * The body of the "Nudge everyone" confirmation.
 */
export function formatNudgeEveryoneConfirm(personCount)
{
	return "This sends an email to " + formatPeopleCount(personCount) + " listing the days they haven't submitted.";
}


/**
 * "n people have time not yet submitted — across the last 5 weeks" (the number of weeks is configurable).
 */
export function formatOutstandingBanner(personCount, windowWeeks)
{
	return formatPeopleCount(personCount) + (personCount === 1 ? " has" : " have") + " time not yet submitted — across " + formatWindowPhrase(windowWeeks);
}


/**
 * A person's line on the Outstanding screen: "Senior Project Manager · 3 days outstanding".
 */
export function formatOutstandingSubline(roleName, dayCount)
{
	const outstanding = formatDayCount(dayCount) + " outstanding";

	return roleName ? roleName + " · " + outstanding : outstanding;
}
